// stores
import * as PractitionerConsumptionStore from '../../../../redux/middleware/PractitionerConsumptionMiddleware';
import * as PractitionerStore from '../../../../redux/middleware/PractitionerMiddleware';
import * as ProtocolsStore from '../../../../redux/middleware/MedicalProtocolMiddleware';

// state
import { AppState } from '../../../../redux/reducers';

// models
import { Practitioner } from '../../../../models/Entities/Practitioners/Practitioner';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { ErrorType } from '../../../../models/HttpError';

//#region Props

interface IProps {
    PractitionerId: string;
    isOpen: boolean;
    onClose: () => void;
    /** Fired after a consumption is stored, so the list can refetch. */
    onSaved?: () => void;
}

interface ConnectedProps {
    Practitioner: Practitioner | undefined;
    /** Protocols the practitioner takes part in — the only ones worth offering. */
    Protocols: Protocol[];

    isSaving: boolean;
    successSaving: boolean | undefined;
    failSaving: boolean;
    errorOnSaving: ErrorType | undefined;

    GetProtocolById: (protocolId: number) => Protocol | null;
}

interface DispatchProps {
    CreatePractitionerConsumption: typeof PractitionerConsumptionStore.ActionCreators.CreatePractitionerConsumption;
    GetProtocolsByPractitionerId: typeof ProtocolsStore.actionCreators.GetProtocolsByPractitionerId;
    GetAllProtocolContractAssociations: typeof ProtocolsStore.actionCreators.GetAllProtocolContractAssociations;
    GetMedicalProtocolById: typeof ProtocolsStore.actionCreators.GetMedicalProtocolById;
}

export type Props = IProps & ConnectedProps & DispatchProps;

// mapStateToProps runs on every dispatched action of the whole app and connect() compares what it
// returns by identity: a new array or a new arrow function here re-renders the drawer on every
// action, which steals focus from whatever field is being typed into.
const memoizeBySource = <TSource, TResult>(compute: (source: TSource) => TResult) => {
    let lastSource: TSource | undefined;
    let lastResult: TResult | undefined;

    return (source: TSource): TResult => {
        if (source !== lastSource || lastResult === undefined) {
            lastSource = source;
            lastResult = compute(source);
        }

        return lastResult as TResult;
    };
};

const getProtocolLookup = memoizeBySource((protocols: Protocol[] | undefined) => {
    const protocolsById = new Map<number, Protocol>((protocols || []).map((protocol: Protocol) => [protocol.id, protocol]));

    return (protocolId: number): Protocol | null => protocolsById.get(protocolId) || null;
});

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const CurrentPractitioner: Practitioner | undefined = state.Practitioners?.list?.find(
        (practitioner: Practitioner) => practitioner.userId === ownProps.PractitionerId
    );

    const getProtocolById = getProtocolLookup(state.MedicalProtocols?.list);

    // The practitioner's own protocols carry no contract association, so the
    // full protocol from MedicalProtocols is preferred whenever it is loaded.
    const practitionerProtocols: Protocol[] = getPractitionerProtocols(CurrentPractitioner?.Protocols, getProtocolById);

    return {
        Practitioner: CurrentPractitioner,
        Protocols: practitionerProtocols,

        isSaving: CurrentPractitioner?.isCreatingConsumption || false,
        successSaving: CurrentPractitioner?.createdConsumptionSuccessfully,
        failSaving: CurrentPractitioner?.failOnCreateConsumption || false,
        errorOnSaving: CurrentPractitioner?.errorOnCreateConsumption,

        GetProtocolById: getProtocolById,
    };
};

// the merged list keeps its identity while neither of its two sources changes
let lastPractitionerProtocolsKey: { source?: Protocol[]; lookup?: (protocolId: number) => Protocol | null } = {};
let lastPractitionerProtocols: Protocol[] = [];

const getPractitionerProtocols = (
    practitionerProtocols: Protocol[] | undefined,
    getProtocolById: (protocolId: number) => Protocol | null
): Protocol[] => {
    if (lastPractitionerProtocolsKey.source === practitionerProtocols && lastPractitionerProtocolsKey.lookup === getProtocolById) {
        return lastPractitionerProtocols;
    }

    lastPractitionerProtocolsKey = { source: practitionerProtocols, lookup: getProtocolById };
    lastPractitionerProtocols = (practitionerProtocols || []).map(
        (protocol: Protocol) => getProtocolById(protocol.id) || protocol
    );

    return lastPractitionerProtocols;
};

export const mapDispatchToProps = {
    ...PractitionerConsumptionStore.ActionCreators,
    ...PractitionerStore.actionCreators,
    ...ProtocolsStore.actionCreators,
};
